import React, { useState, useEffect, useCallback, useContext } from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { isSafari, isMobileSafari, isIOS } from 'react-device-detect';
import { Dropdown, Menu, Tag, Collapse, Radio, Form, Input } from 'antd';
import Icon from '@ant-design/icons';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import useMedia from 'use-media';
import { mediaHook } from 'customHooks';

import {
  isSllrUser,
  getAccountTypePropForSuccessMetrics,
  SLLR_ICON
} from 'utils/helpers';
import { useModal } from 'contexts/modalProvider.context';
import { sendSegment } from 'utils/segment';
import { setLocaleInLocalStorage } from 'utils/intl-wrapper';
import { checkRoleBannerModal } from 'utils/teams-and-member';
import { isUserFullyActivated } from 'utils/features-access';
import { LOCALE } from 'constants/intl-wrapper';
import { CONNECTED_DOMAIN } from 'constants/sllr';
import { ANNOUNCEMENT_MODAL_TEAM } from 'constants/teams-and-members';
import { PRODUCTION_URL } from 'constants/login';
import { DRAFT_ORDER, CREATION_SRC } from 'constants/marketplace';
import { newNotification, readNotification } from 'actions/index';
import { askForPermissioToReceiveNotifications } from 'Firebase/push-notification';
import { ContextWrapper } from 'contexts/wrapper.context';
import { LayoutWrapper } from 'contexts/layout.context';
import { submitFeedbackAboutBeta } from 'services/auth';
import { getDraftOrders } from 'services/draft-orders';
import { requiredRule, whiteSpaceRule } from 'utils/forms';
import {
  getDiscountsAccess,
  getOrdersAndPickupsAccess,
  getProductsAndCategoriesAccess,
  userHasTabAccess
} from 'utils/permissions/permissions-apply';
import { PERMISSIONS_MODULES_ID } from 'constants/team-members';

import BRButton from 'components/BRButton/BRButton';
import { notify } from 'components/Notify/Notify';
import Notifications from 'components//Notifications/Notifications';
import TeamsMemberAnnouncement from 'components/Settings/components/TeamsMembers/components/TeamsMemberAnnouncement/TeamsMemberAnnouncement';
import SubscriptionAnnouncementModal from 'components/Settings/components/Subscriptions/components/SubscriptionAnnouncementModal/SubscriptionAnnouncementModal';
import ActivationModal from 'components/BRModals/ActivationModal/ActivationModal';
import BookCall from 'components/BookCall/BookCall';
import ConfirmationModal from 'components/BRModals/ConfirmationModal/ConfirmationModal';
import AddProductModal from 'components/Products/components/AddProductModal/AddProductModal';
import ImportMultipleProductsModal from 'components/Products/components/ImportMultipleProductsModal/ImportMultipleProductsModal';
import { openLiveDNSModal } from 'components/Products/components/SllrStore/components/SllrSettings/components/SllrDomains/components/DomainsModal';
import NeedHelp from './NeedHelp/NeedHelp';

import { ReactComponent as Notification } from 'assets/bosta-icons/Notification.svg';
import { ReactComponent as Plus } from 'assets/bosta-icons/Plus.svg';
import { ReactComponent as Down } from 'assets/bosta-icons/Down.svg';
import { ReactComponent as ArrowRight } from 'assets/bosta-icons/Arrow-right.svg';
import { ReactComponent as Left } from 'assets/bosta-icons/Left.svg';
import { ReactComponent as SingleOrderIcon } from 'assets/bosta-icons/Orders.svg';
import { ReactComponent as CreatePickupIcon } from 'assets/bosta-icons/Pickups.svg';
import { ReactComponent as DropdownIcon } from 'assets/bosta-icons/Dropdown.svg';
import { ReactComponent as LanguageIcon } from 'assets/bosta-icons/Language.svg';
import { ReactComponent as Logout } from 'assets/bosta-icons/logout.svg';
import { ReactComponent as Rocket } from 'assets/bosta-icons/rocket.svg';
import { ReactComponent as DiscountIcon } from 'assets/bosta-icons/Discount.svg';
import { ReactComponent as ProductIcon } from 'assets/bosta-icons/Product.svg';

import './Header.less';

const { DELIVERIES, PRODUCTS, DISCOUNTS } = PERMISSIONS_MODULES_ID;

const Header = ({
  title,
  subTitle,
  history,
  needHelp,
  setNewNotification,
  setReadNotification,
  newNotification,
  withBackArrow = false,
  goBackTo,
  intl,
  titleClassName = null,
  extraHeaderContent = null,
  hideRightSide = false,
  customRightSideContent,
  className,
  headerContent
}) => {
  const isMobileScreen = useMedia({ maxWidth: '768px' });
  const isSmallMobileScreen = useMedia({ maxWidth: '576px' });

  const [isNotificationUnread, setIsNotificationUnread] = useState(false);
  const [isCreateButtonHovered, setIsCreateButtonHovered] = useState(false);
  const [isCreateMenuVisible, setIsCreateMenuVisible] = useState(false);

  const { openModal } = useModal();

  const userData = JSON.parse(localStorage.getItem('userInfo'));
  const userInfo = userData?.user;
  const isTeamsAnnouncementShown = JSON.parse(
    localStorage.getItem('TeamsAnnouncementShown')
  );
  const isSubscriptionAnnouncementShown = JSON.parse(
    localStorage.getItem('SllrPaidSubscriptionAnnouncementShown')
  );
  const {
    businessInfo,
    setBusinessInfo,
    newFeatures,
    setDraftOrdersCount,
    handleOpenCreateEditPickupModal,
    isSllrSubscribed
  } = useContext(ContextWrapper);
  const { isContainerSliding } = useContext(LayoutWrapper);

  const { HIDE_CREATE_DISCOUNT } = getDiscountsAccess(
    businessInfo?.userSllrPermissions
  );

  const { HIDE_CREATE_PRODUCT } = getProductsAndCategoriesAccess(
    businessInfo?.userSllrPermissions
  );

  const { HIDE_CREATE_ORDER, HIDE_SCHEDULE_PICKUP } = getOrdersAndPickupsAccess(
    businessInfo?.userSllrPermissions
  );

  const userSllrLink = businessInfo?.storeInfo?.storeLink;

  const hasProducts = !!businessInfo?.productsCount;
  const shouldRenderCreateMenu =
    !isSllrUser() || businessInfo.isFirstPickupCreated;

  useEffect(() => {
    setIsNotificationUnread((prev) => {
      return newNotification;
    });
  }, [newNotification]);

  useEffect(() => {
    const isLanguageChanged = localStorage.getItem('languageChanged');
    if (!!isLanguageChanged) {
      notify({
        title: intl.formatMessage({
          id: 'new_header.profile.change_language_successfully'
        }),
        type: 'success'
      });
    }
    localStorage.removeItem('languageChanged');
  }, [intl]);

  useEffect(() => {
    if (
      newFeatures &&
      Object.keys(newFeatures).includes('teamsmembers') &&
      !isTeamsAnnouncementShown &&
      checkRoleBannerModal(ANNOUNCEMENT_MODAL_TEAM)
    ) {
      openModal(TeamsMemberAnnouncement, {
        onSuccess: () => {
          history.push('/settings/teams-members');
        }
      });
    }
  }, [isTeamsAnnouncementShown, history, newFeatures]);

  useEffect(() => {
    if (!isSubscriptionAnnouncementShown && isSllrSubscribed === false) {
      localStorage.setItem('SllrPaidSubscriptionAnnouncementShown', true);
      sendSegment('E_SLLR_PAID_ANNOUNCEMENT_VIEWED');
      openModal(SubscriptionAnnouncementModal, {
        onSubscribe: () => {
          history.push('/settings/subscription');
        }
      });
    }
  }, [isSubscriptionAnnouncementShown, history, isSllrSubscribed]);

  async function fetchDraftOrdersCount() {
    try {
      const { total } = await getDraftOrders({
        creationSrc: CREATION_SRC,
        isSelfManagedShipping: false,
        isConfirmed: false
      });
      setDraftOrdersCount(total);

      setBusinessInfo((prev) => ({
        ...prev,
        featuresConfigurations: {
          ...prev?.featuresConfigurations,
          isFirstSllrOrderReceived: true
        }
      }));
    } catch (error) {
      notify({ msg: error.message, error });
    }
  }

  const handleNotificationEvent = (event) => {
    setNewNotification();
    if (
      event?.data?.data?.body?.includes(DRAFT_ORDER) &&
      userHasTabAccess(DELIVERIES, businessInfo?.userSllrPermissions)
    ) {
      fetchDraftOrdersCount();
    }
    if (event?.data?.data?.click_action === CONNECTED_DOMAIN) {
      openLiveDNSModal({
        domainLink: event?.data?.data?.domainLink,
        sllrURL: userSllrLink,
        intl,
        openModal
      });
    }
  };

  useEffect(() => {
    if (!isSafari && !isMobileSafari && !isIOS) {
      askForPermissioToReceiveNotifications();
      navigator.serviceWorker &&
        navigator.serviceWorker.addEventListener('message', (event) => {
          handleNotificationEvent(event);
        });

      const channel = new BroadcastChannel('firebase-messaging-sw');
      channel.addEventListener('message', (event) => {
        handleNotificationEvent(event);
      });
    }
  }, [setNewNotification]);

  const handleCreateSingleOrderClick = () => {
    sendSegment('E_SINGLE_ORDER_SELECTED');
    history.push('/create-order');
  };

  const handleMultipleOrderClick = () => {
    sendSegment('E_UPLOAD_MULTIPLE_ORDERS_SELECTED');
    // !userInfo?.businessAdminInfo?.fulfillmentInfo?.isActive &&
    history.push('/orders/upload/smart-massupload');
  };

  const handleCreatePickupClick = () => {
    if (!isUserFullyActivated) {
      openModal(ActivationModal, {
        icon: <Icon component={Rocket} />,
        title: intl.formatMessage({
          id: 'new_header.orders.activation_modal.title'
        }),
        subtitle: intl.formatMessage({
          id: 'new_header.orders.activation_modal.subtitle'
        }),
        buttonText: intl.formatMessage({
          id: 'new_header.orders.activation_modal.buttonText'
        })
      });
    } else {
      handleOpenCreateEditPickupModal({ openModal });
      sendSegment('E_CREATE_PICKUP_SELECTED');
    }
  };

  const handleGoBackClick = () => {
    if (goBackTo) {
      history.push(goBackTo);
    } else {
      history.goBack();
    }
  };

  const checkIfPageWithCreateButton = useCallback(() => {
    const pagesNotContainsCreateButton = ['settings'];
    return !pagesNotContainsCreateButton.some(
      (page) =>
        history.location.pathname.includes(page) &&
        !history.location.pathname.includes('sllr')
    );
  }, [history]);

  const openActivationModal = () => {
    openModal(ActivationModal, {
      icon: <Icon component={Rocket} />,
      title: intl.formatMessage({
        id: 'new_header.orders.activation_modal.title'
      }),
      subtitle: intl.formatMessage({
        id: 'new_header.orders.activation_modal.subtitle'
      }),
      buttonText: intl.formatMessage({
        id: 'new_header.orders.activation_modal.buttonText'
      })
    });
  };

  const handleProductsMenuClick = ({ key, hoverOrClick }) => {
    if (isSllrUser() || isUserFullyActivated) {
      switch (key) {
        case '0': //create-single
          return (() => {
            sendSegment(`PRODUCTS_ADD-SINGLE_${hoverOrClick}`);
            history.push('/products/create');
          })();
        case '1': //create-multiple
          return (() => {
            sendSegment(`open_product_bulk_upload}`);
            openModal(ImportMultipleProductsModal, {
              openAddProductModal: handleAddProductClick,
              onSuccess: () => history.push('/products/catalog')
            });
          })();
        default:
          return;
      }
    } else {
      openActivationModal();
    }
  };

  const handleAddProductClick = () => {
    if (isSllrUser() || isUserFullyActivated) {
      openModal(AddProductModal, { onCardClick: handleProductsMenuClick });
    } else {
      openActivationModal();
    }
  };

  const handleAddDiscountClick = () => {
    history.push('/discounts');
  };

  const createNewMenu = useCallback(
    () => (
      <Menu className="header__menu">
        {!HIDE_CREATE_PRODUCT &&
          userHasTabAccess(PRODUCTS, businessInfo?.userSllrPermissions) && (
            <Menu.Item
              onClick={handleAddProductClick}
              className="display-flex align-center justify-flex-start"
            >
              <div className="display-flex flex-dir-col">
                <div className="display-flex">
                  <div className="br-header__multiple-order__icon-container">
                    <Icon
                      className="ant-icon-lg br-header__menu__multiple-order-options__icon"
                      component={ProductIcon}
                    />
                  </div>
                  <div className="display-flex flex-dir-col">
                    <span className="header__menu__title">
                      {intl.formatMessage({
                        id: 'new_header.actions.new_product'
                      })}
                    </span>
                    <span className="header__menu__sub-title">
                      {intl.formatMessage({
                        id: 'new_header.actions.new_product_subtitle'
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </Menu.Item>
          )}
        {userHasTabAccess(DISCOUNTS, businessInfo?.userSllrPermissions) &&
          !HIDE_CREATE_DISCOUNT && (
            <Menu.Item
              onClick={handleAddDiscountClick}
              className="display-flex align-center justify-flex-start"
            >
              <div className="display-flex flex-dir-col">
                <div className="display-flex">
                  <div className="br-header__multiple-order__icon-container">
                    <Icon
                      className="ant-icon-lg non_fill "
                      component={DiscountIcon}
                    />
                  </div>
                  <div className="display-flex flex-dir-col">
                    <span className="header__menu__title">
                      {intl.formatMessage({
                        id: 'new_header.actions.new_discount'
                      })}
                    </span>
                    <span className="header__menu__sub-title">
                      {intl.formatMessage({
                        id: 'new_header.actions.new_discount_subtitle'
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </Menu.Item>
          )}
        {!HIDE_CREATE_ORDER && (
          <Menu.Item
            onClick={handleCreateSingleOrderClick}
            className="display-flex align-center justify-flex-start"
            icon={
              <Icon
                className="ant-icon-lg br-header-create-menu__icons"
                component={SingleOrderIcon}
              />
            }
          >
            <div className="display-flex flex-dir-col">
              <span className="header__menu__title">
                {intl.formatMessage({
                  id: `new_header.actions.single_order_sllr`
                })}
              </span>
              <span className="header__menu__sub-title">
                {intl.formatMessage({
                  id: `new_header.actions.single_order_sub_title_sllr`
                })}
              </span>
            </div>
          </Menu.Item>
        )}

        {!HIDE_SCHEDULE_PICKUP && (
          <Menu.Item
            onClick={handleCreatePickupClick}
            className="display-flex align-center justify-flex-start"
            icon={
              <Icon
                className="ant-icon-lg br-header-create-menu__icons"
                component={CreatePickupIcon}
              />
            }
          >
            <div className="display-flex flex-dir-col">
              <span className="header__menu__title">
                {intl.formatMessage({
                  id: 'new_header.actions.pickup'
                })}
              </span>
              <span className="header__menu__sub-title">
                {intl.formatMessage({
                  id: 'new_header.actions.pickup_sub_title'
                })}
              </span>
            </div>
          </Menu.Item>
        )}
      </Menu>
    ),
    [history, intl]
  );

  const handleLanguageChange = useCallback(async (value) => {
    await setLocaleInLocalStorage(value);
    localStorage.setItem('languageChanged', true);
    window.location.reload();
  }, []);

  const logoutOnClick = useCallback(
    (url) => {
      history.push(url);
    },
    [history]
  );

  const profileDropdown = () => {
    let userEmail = userInfo?.emails[0]?.address;
    const separator = '...',
      acceptableStringLen = 35;
    const separatorLength = separator.length,
      charsToShow = acceptableStringLen - separatorLength,
      frontChars = Math.ceil(charsToShow / 2),
      backChars = Math.floor(charsToShow / 2);
    if (userEmail.length > acceptableStringLen) {
      userEmail =
        userEmail.substr(0, frontChars) +
        separator +
        userEmail.substr(userEmail.length - backChars);
    }

    return (
      <>
        <div className="br-header__profile-dropdown shadow-md">
          <div className="br-header__profile-user-info">
            <div className="br-header__profile-user-name body-medium">
              {userInfo?.profile?.firstName} {userInfo?.profile?.lastName}
            </div>
            <div className="br-header__profile-user-email caption">
              {userEmail}
            </div>
          </div>
          <Collapse
            accordion
            bordered={false}
            ghost={true}
            expandIconPosition="end"
            expandIcon={({ isActive }) => (
              <Icon
                component={DropdownIcon}
                rotate={
                  isActive
                    ? intl.locale === LOCALE.EN
                      ? 360
                      : 0
                    : intl.locale === LOCALE.EN
                    ? 270
                    : 90
                }
              />
            )}
          >
            <Collapse.Panel
              header={
                <div className="br-header__profile-language-container">
                  <div className="br-header__profile-language">
                    <div className="br-header__profile-language-icon">
                      <LanguageIcon />
                    </div>
                    <div className="br-header__profile-language-container__row body">
                      {intl.formatMessage({
                        id: 'new_header.profile.select_language.title'
                      })}
                    </div>
                  </div>
                  <div className="br-header__profile-lang subheading">
                    {intl.formatMessage({
                      id: `new_header.profile.select_language.${intl.locale.toLowerCase()}.abbreviation`
                    })}
                  </div>
                </div>
              }
              key={1}
            >
              <Radio.Group
                defaultValue={intl.locale}
                onChange={({ target }) => handleLanguageChange(target.value)}
              >
                <Radio value={LOCALE.EN}>
                  {intl.formatMessage({
                    id: 'new_header.profile.select_language.en.label'
                  })}
                </Radio>
                <Radio value={LOCALE.AR}>
                  {intl.formatMessage({
                    id: 'new_header.profile.select_language.ar.label'
                  })}
                </Radio>
              </Radio.Group>
            </Collapse.Panel>
          </Collapse>
          <div
            className="br-header__profile-logout-container"
            onClick={() => logoutOnClick('/logout')}
          >
            <div className="br-header__profile-logout body">
              <div className="br-header__profile-logout-icon">
                <Logout />
              </div>
              {intl.formatMessage({
                id: 'new_header.profile.logout'
              })}
            </div>
          </div>
        </div>
      </>
    );
  };

  const handleSubmitFeedback = async ({ feedback }) => {
    try {
      await submitFeedbackAboutBeta({ feedback });
      const userToken = localStorage.getItem('newToken');
      sendSegment('BETA_SWITCH-TO-NORMAL_CONFIRMED');
      userData.user.businessAdminInfo = {
        ...userData?.user?.businessAdminInfo,
        isBetaUser: false
      };
      localStorage.setItem('userInfo', JSON.stringify(userData));
      window.open(
        `${PRODUCTION_URL}${userToken.replace('Bearer ', '')}&fromBeta=true`,
        '_parent'
      );
    } catch (error) {
      notify({ msg: error.message, error });
    }
  };

  const onOpenFeedBackModal = () => {
    sendSegment('BETA_SWITCH-TO-NORMAL');
    openModal(ConfirmationModal, {
      title: (
        <>
          <div className="display-xs">
            {intl.formatMessage({
              id: 'beta_flow.switch_to_normal_dashboard'
            })}
          </div>
          <div className="body br-feedback-modal-subtitle">
            {intl.formatMessage({ id: 'beta_flow.no_problem' })}
          </div>
        </>
      ),
      content: (
        <Form id="feedback-form" onFinish={handleSubmitFeedback}>
          <Form.Item
            name="feedback"
            label={intl.formatMessage({
              id: 'beta_flow.tell_us_why_switching'
            })}
            rules={[
              requiredRule(
                intl.formatMessage({
                  id: 'beta_flow.feedback_validation'
                })
              ),
              whiteSpaceRule(
                intl.formatMessage({
                  id: 'beta_flow.feedback_validation'
                })
              )
            ]}
          >
            <Input.TextArea
              placeholder={intl.formatMessage({
                id: 'beta_flow.feedback_placeholder'
              })}
              autoSize={{ minRows: 3, maxRows: 3 }}
            />
          </Form.Item>
        </Form>
      ),
      confirmButtonLabel: intl.formatMessage({ id: 'common.confirm' }),
      confirmButtonProps: {
        type: 'primary',
        form: 'feedback-form',
        htmlType: 'submit',
        onClick: () => {}
      }
    });
  };

  return (
    <div
      className={classnames(
        'br-header font-family__default',
        'border-bottom',
        className,
        {
          'br-header__collapse': isContainerSliding
        }
      )}
    >
      {headerContent ? (
        headerContent()
      ) : (
        <>
          <h1>
            {withBackArrow && (
              <>
                <Icon
                  component={Left}
                  onKeyDown={handleGoBackClick}
                  onClick={handleGoBackClick}
                  className="br-header__go-back-btn ant-icon-md "
                />
              </>
            )}
            <div
              className={classnames('br-header__logo-title-container', {
                'br-header__logo-title-container__go-back': withBackArrow
              })}
            >
              {isMobileScreen &&
                SLLR_ICON({
                  asComponent: true,
                  size: 'medium'
                })}

              <div className="display-flex flex-dir-col">
                <span
                  className={classnames(
                    'br-header__logo-title-container__title display-sm',
                    {
                      [titleClassName]: titleClassName
                    }
                  )}
                >
                  {title}
                </span>
                {!!subTitle && (
                  <span className="br-header__logo-title-container__sub-title body">
                    {subTitle}
                  </span>
                )}
              </div>
            </div>
          </h1>
          {!hideRightSide && (
            <div className="br-header__right-section display-flex align-center">
              {needHelp && <NeedHelp />}

              {/* {!isSmallMobileScreen && !businessInfo.isFirstPickupPickedUp && (
                <BookCall
                  segmentSource={`${
                    isSllrUser() ? 'Sllr' : 'Bosta'
                  } Account - Header`}
                />
              )} */}

              {(isSllrUser()
                ? userSllrLink && hasProducts && !userInfo?.isUserFullyActivated
                : !userInfo?.isUserFullyActivated) && (
                <Tag.CheckableTag
                  className="ant-tag-yellow br-header__right-section__activate-account"
                  onChange={() => {
                    if (isSllrUser()) {
                      sendSegment('Step0_Setup_Delivery_Service', {
                        Activate_Source: 'Topbar',
                        ...getAccountTypePropForSuccessMetrics()
                      });
                    }
                    history.push('/business-information');
                  }}
                >
                  {intl.formatMessage({
                    id: isSllrUser()
                      ? `sign_up.email_verification.sllr_activate_account_sllr_dashboard`
                      : 'new_header.actions.activate_account'
                  })}
                  <ArrowRight />
                </Tag.CheckableTag>
              )}
              {extraHeaderContent && extraHeaderContent}
              <Dropdown
                trigger={['click']}
                overlayClassName="br-header__notification"
                placement={
                  intl.locale === LOCALE.AR ? 'bottomLeft' : 'bottomRight'
                }
                overlay={() => <Notifications />}
                getPopupContainer={(trigger) =>
                  trigger.parentElement.parentElement.parentElement
                    .parentElement
                }
                destroyPopupOnHide
                onVisibleChange={(open) => {
                  if (open && !!newNotification) {
                    setReadNotification();
                  }
                }}
              >
                <div className="br-header__notification-icon">
                  <Icon className="ant-icon-md" component={Notification} />
                  {!!isNotificationUnread && (
                    <div className="br-header__notification-icon--unread"></div>
                  )}
                </div>
              </Dropdown>
              <Dropdown
                trigger={['click']}
                placement={
                  intl.locale === LOCALE.AR ? 'bottomLeft' : 'bottomRight'
                }
                overlay={profileDropdown}
                className="br-header__user-name body-medium"
              >
                <div>
                  {userInfo?.profile?.firstName?.charAt(0).toUpperCase() || ''}
                  {userInfo?.profile?.lastName.charAt(0).toUpperCase() || ''}
                </div>
              </Dropdown>
              {checkIfPageWithCreateButton() && (
                <>
                  {isSmallMobileScreen && shouldRenderCreateMenu && (
                    <div className="br-header__separator"></div>
                  )}
                  {shouldRenderCreateMenu && (
                    <Dropdown
                      overlay={createNewMenu}
                      placement={
                        intl.locale === LOCALE.AR ? 'bottomLeft' : 'bottomRight'
                      }
                      trigger={isMobileScreen ? ['click'] : ['hover']}
                      type="brand-button"
                      className={classnames(
                        'display-flex align-center br-header__create-button',
                        {
                          'br-header__create-button--ltr':
                            intl.locale === LOCALE.EN
                        },
                        {
                          'br-header__create-button--rtl':
                            intl.locale === LOCALE.AR
                        }
                      )}
                      onVisibleChange={(visible) =>
                        setIsCreateMenuVisible(visible)
                      }
                      onMouseEnter={() => setIsCreateButtonHovered(true)}
                      onMouseLeave={() => {
                        setIsCreateButtonHovered(false);
                      }}
                    >
                      <BRButton
                        type="primary"
                        className="button"
                        onClick={() => {
                          sendSegment('E_CREATE_NEW');
                        }}
                        // label={intl.formatMessage({
                        //   id: 'new_header.actions.title'
                        // })}
                        suffixIcon={
                          isCreateButtonHovered || isCreateMenuVisible ? (
                            <Down />
                          ) : (
                            <Plus />
                          )
                        }
                      />
                    </Dropdown>
                  )}
                </>
              )}
            </div>
          )}
          {customRightSideContent}
        </>
      )}
    </div>
  );
};

Header.propTypes = {
  title: PropTypes.string,
  history: PropTypes.shape({
    goBack: PropTypes.func,
    push: PropTypes.func,
    location: PropTypes.shape({
      pathname: PropTypes.string
    })
  }),
  // listRoutes: PropTypes.shape([]),
  withBackArrow: PropTypes.bool,
  uploadCsv: PropTypes.bool
};

Header.defaultProps = {
  title: '',
  history: {},
  listRoutes: [],
  withBackArrow: false,
  uploadCsv: false
};

const mapDispatchToProps = (dispatch) => ({
  setNewNotification: () => dispatch(newNotification()),
  setReadNotification: () => dispatch(readNotification())
});

const mapStateToProps = ({ notification }) => {
  return { ...notification };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(injectIntl(mediaHook(Header))));
